import axios from "axios";
import { currentUserPlannerToken, currentUserToken } from "../utils/constants";
import { refreshPlannerToken } from "./apiGoogleAuth";
import { localStorageInstance } from "../utils/localStorage";
import { refreshToken } from "./apiAuth";

const API_URL =  process.env.REACT_APP_PLANNER_BASE_URL;

const Api = axios.create({
  baseURL: API_URL || "",
});

// Store original requests that failed with 401 to retry them
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

const plannerTokenSharingEnabled = localStorageInstance.getItem("planner_token_sharing_enabled");

Api.interceptors.request.use(function (response) {
  const token = currentUserPlannerToken();
  const taskboardToken = currentUserToken();
  if ((token !== undefined && token !== null) || (taskboardToken !== undefined && taskboardToken !== null)) {
    response.headers["authorization"] = `Bearer ${plannerTokenSharingEnabled ? taskboardToken : token ? token : taskboardToken}`;
  }
  return response;
});


Api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing the token, add this request to the queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers["authorization"] = `Bearer ${token}`;
            return Api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }
      
      // Mark this request as retried to avoid infinite loops
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Try to refresh the token
        let freshTocken;
        if(plannerTokenSharingEnabled){
          const refresh_token = localStorageInstance.getItem("refresh_token");
          const res = await refreshToken(refresh_token);
          const newToken = res?.data?.data?.responseData[0]?.token?.access_token;
  
          localStorageInstance.setItem({ key: "token", value: newToken });
          localStorageInstance.setItem({ key: "refresh_token", value: res.data.data.responseData[0].token.refresh_token });
        }else{
          freshTocken = await refreshPlannerToken();
        }
        
        // Update the header for this request
        originalRequest.headers["authorization"] = `Bearer ${freshTocken}`;
        
        // Process any queued requests with the new token
        processQueue(null, freshTocken);
        
        // Return the original request with the new token
        return Api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, process the queue with the error
        processQueue(refreshError, null);
        
        // Redirect to login or handle as needed
        console.error("Token refresh failed:", refreshError);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);



export const getPlannerTodos = async () => {
  return await Api.get(`/api/v1/todos?include_completed=true`);
};

export const postPlannerTodos = async (data) => {
  return await Api.post(`/api/v1/todos`, data);
};

export const putPlannerTodos = async (data) => {
  return await Api.put(
    `/api/v1/todos/${data.id}${data?.isMove ? "/move" : ""}`,
    data.data
  );
};

export const deletePlannerTodos = async (id) => {
  return await Api.delete(`/api/v1/todos/${id}`);
};

export const postPlannerTodosNotes = async (data) => {
  return await Api.post(`/api/v1/notes`, data);
};

export const putPlannerTodosNotes = async (data) => {
  return await Api.put(`/api/v1/notes/${data.id}`, data.data);
};

// Global notes API methods
export const getPlannerGlobalNotes = async () => {
  return await Api.get(`/api/v1/notes`);
};

export const deletePlannerTodosNotes = async (id) => {
  return await Api.delete(`/api/v1/notes/${id}`);
};
